/**
 * launchGuards — per-workflow launch preconditions, shared by every host.
 *
 * Some workflows can't run without a resolved client context (e.g. the carrier
 * mortgagee update, which acts on a specific policy/account). This is the single
 * place that declares which workflows require a client so the launch surface can
 * disable their catalog cards until a client is selected. Add a workflow's
 * `workflow_id`/`ref` here to enforce the same gate on it.
 *
 * Was add-in only (per operator decision) until story 26752's
 * `workflows.triage.$messageId` route became ora's second consumer of
 * `workflowRequiresClient` — the SAME check, not a re-declared one.
 */

/**
 * workflow_id / ref values that must have a client selected before launch.
 *
 * These MUST match the workflow ids the pilot catalog actually surfaces
 * (workflows/src/workflows/tools/list_workflows.py PUBLISHED_WORKFLOW_IDS).
 * Briefs and email extraction now recommend `carrier_mortgagee_update_v2`
 * (AM-facing "Carrier Mortgagee Update v2"). `mortgagee_update_v1_2` and
 * legacy `carrier_mortgagee_update` remain gated so a leftover catalog click
 * cannot skip the client requirement. The catalog descriptor's `id` carries
 * the workflow_id.
 */
const WORKFLOWS_REQUIRING_CLIENT: ReadonlySet<string> = new Set([
  "mortgagee_update_v1_2",
  "carrier_mortgagee_update_v2",
  "carrier_mortgagee_update",
]);

/** The AM-facing mortgagee workflow — grouped launch form + v2 title. */
export const V15_MORTGAGEE_WORKFLOW_ID = "carrier_mortgagee_update_v2";

const LEGACY_MORTGAGEE_RECOMMENDATIONS: ReadonlySet<string> = new Set([
  "mortgagee_update_v1_2",
  "carrier_mortgagee_update",
]);

/** Test-tool recommendations that remap onto their v2 (kept out of the mortgagee family). */
const TEST_TOOL_V2_RECOMMENDATIONS: Readonly<Record<string, string>> = {
  epic_reset_test_policy: "epic_reset_test_policy_v2",
};

/**
 * Map a cached / extracted recommendation onto v2. Existing briefs still
 * carry `mortgagee_update_v1_2`; opening that id renders the flat form.
 */
export function canonicalRecommendedWorkflowId(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  if (LEGACY_MORTGAGEE_RECOMMENDATIONS.has(raw)) return V15_MORTGAGEE_WORKFLOW_ID;
  return TEST_TOOL_V2_RECOMMENDATIONS[raw] ?? raw;
}

/**
 * The catalog entry for an already-canonical recommended id. An EXACT id match
 * always wins; only when none exists does an entry that merely remaps onto it
 * (a legacy id) stand in — so an unpublished v1 listed before the v2 can never
 * be chosen over it.
 */
export function findRecommendedCatalogEntry<T extends { id: string }>(
  catalog: readonly T[],
  recommendedId: string,
): T | null {
  return (
    catalog.find((entry) => entry.id === recommendedId) ??
    catalog.find((entry) => canonicalRecommendedWorkflowId(entry.id) === recommendedId) ??
    null
  );
}

/** True when the given catalog entry may only launch with a client selected. */
export function workflowRequiresClient(entry: {
  id?: string | null;
  ref?: string | null;
} | null | undefined): boolean {
  if (!entry) return false;
  return (
    (!!entry.id && WORKFLOWS_REQUIRING_CLIENT.has(entry.id)) ||
    (!!entry.ref && WORKFLOWS_REQUIRING_CLIENT.has(entry.ref))
  );
}
